import React, { useEffect, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { DateTime } from 'luxon';
import { db } from '../firebase';
import Avatar from './Avatar';
import VoiceNote from './VoiceNote';
import { leaveGroup } from '../services/groupMatching';
import GroupMatchingPage from './GroupMatchingPage';
import { useLanguage } from '../i18n';

const GROUP_MSG_LIMIT = 20;
const GROUP_VOICE_LIMIT = 5;

const PAYSTACK_PUBLIC_KEY = 'pk_live_ced368f17237c0a072b6db63d641ca539b9ca6be';
const MONTHLY_PLAN_CODE = 'PLN_fdt81n4wnr0z85q';
const SIX_MONTH_PLAN_CODE = 'PLN_prih2qg54fputl9';

// ─────────────────────────────────────────────────────────────
// Client-side mirror of the backend's isNewLocalDay()/normalizeTimezone()
// in functions/index.js. The backend only resets groupMessagesUsedToday /
// groupVoiceNotesUsedToday lazily, on the first group/community send of a
// new day — so the user doc can sit at yesterday's count all day until
// someone actually sends something. Without this check, the UI displays
// that stale count as if it were still accurate. This does NOT touch
// Firestore — it only decides what to show, the same way the backend
// decides whether to reset before checking the limit.
// ─────────────────────────────────────────────────────────────
function normalizeTimezone(tz) {
  if (!tz || typeof tz !== 'string') return 'UTC';
  if (DateTime.now().setZone(tz).isValid) return tz;
  const match = tz.match(/^GMT([+-]\d{1,2}(:\d{2})?)$/i);
  if (match) {
    const converted = `UTC${match[1]}`;
    if (DateTime.now().setZone(converted).isValid) return converted;
  }
  return 'UTC';
}

function isNewLocalDay(lastReset, timezone) {
  const zone = normalizeTimezone(timezone);
  if (!lastReset) return true;
  const lastDate = lastReset.toDate ? lastReset.toDate() : new Date(lastReset);
  const last = DateTime.fromJSDate(lastDate, { zone });
  const now = DateTime.now().setZone(zone);
  return last.toISODate() !== now.toISODate();
}

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
  @keyframes messageIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .group-input:focus {
    border-color: #008081 !important;
    box-shadow: 0 0 0 3px rgba(0,128,129,0.15) !important;
    outline: none !important;
  }
  .group-send-btn {
    transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s !important;
  }
  .group-send-btn:hover:not(:disabled) {
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 20px rgba(0,128,129,0.4) !important;
  }
  .group-icon-btn {
    transition: background-color 0.2s, border-color 0.2s !important;
  }
  .group-icon-btn:hover {
    border-color: rgba(0,128,129,0.5) !important;
    background-color: rgba(0,128,129,0.08) !important;
  }
  .group-header-btn {
    transition: background-color 0.2s, border-color 0.2s !important;
  }
  .group-header-btn:hover {
    background-color: rgba(0,128,129,0.08) !important;
    border-color: rgba(0,128,129,0.3) !important;
  }
  .group-member-card {
    transition: border-color 0.2s !important;
  }
  .group-member-card:hover {
    border-color: rgba(0,128,129,0.3) !important;
  }
  .premium-plan-btn {
    transition: transform 0.2s, box-shadow 0.2s !important;
  }
  .premium-plan-btn:hover:not(:disabled) {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(0,0,0,0.3) !important;
  }
  .strike-modal-btn {
    transition: transform 0.2s, box-shadow 0.2s !important;
  }
  .strike-modal-btn:hover {
    transform: translateY(-1px) !important;
  }
`;

function PremiumModal({ onClose, reason, theme, userEmail, userId }) {
  const { t: translate } = useLanguage();
  const isDark = theme === 'dark';
  const [paystackLoaded, setPaystackLoaded] = useState(false);

  useEffect(() => {
    if (window.PaystackPop) { setPaystackLoaded(true); return; }
    const existing = document.getElementById('paystack-inline-script');
    if (existing) {
      existing.addEventListener('load', () => setPaystackLoaded(true));
      return;
    }
    const script = document.createElement('script');
    script.id = 'paystack-inline-script';
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => setPaystackLoaded(true);
    document.body.appendChild(script);
  }, []);

  const textColor       = isDark ? '#ffffff' : '#1a1a2e';
  const textSecondary   = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';
  const bgColor         = isDark ? 'linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))' : 'linear-gradient(145deg, #ffffff, #f0f0f0)';
  const borderColor     = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const featureBg       = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  const featureBorder   = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const featureText     = isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)';
  const closeBtnBorder  = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)';
  const closeBtnText    = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)';

  const handleUpgrade = (planCode, planName) => {
    if (!paystackLoaded || !window.PaystackPop) {
      alert('Payment system is loading, please try again in a moment.');
      return;
    }
    const amount = planCode === MONTHLY_PLAN_CODE ? 64500 : 258000;
    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email: userEmail,
      amount: amount,
      currency: 'KES',
      plan: planCode,
      metadata: {
        custom_fields: [
          { display_name: 'User ID', variable_name: 'userId', value: userId },
          { display_name: 'Plan Name', variable_name: 'planName', value: planName }
        ]
      },
      callback: function(response) {
        console.log('Payment successful:', response.reference);
      },
      onClose: function() {}
    });
    handler.openIframe();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem',
      animation: 'fadeInUp 0.25s ease'
    }}>
      <div style={{
        background: bgColor,
        backdropFilter: 'blur(20px)',
        borderRadius: '1.5rem', padding: '2rem',
        maxWidth: '380px', width: '100%',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
        border: `1px solid ${borderColor}`
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <div style={{
            width: '64px', height: '64px', margin: '0 auto 1rem',
            background: 'linear-gradient(135deg, #008081, #4B2C5E)',
            borderRadius: '50%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '1.75rem',
            boxShadow: '0 8px 24px rgba(0,128,129,0.35)'
          }}>✨</div>
          <h2 style={{ margin: 0, color: textColor, fontSize: '1.35rem', fontWeight: '800', letterSpacing: '-0.02em' }}>
            {translate('upgradeToPremium')}
          </h2>
          <p style={{ margin: '0.5rem 0 0', color: textSecondary, fontSize: '0.875rem', lineHeight: 1.6 }}>
            {reason === 'groupMessages' && translate('dailyGroupMessageLimitReached')}
            {reason === 'groupVoice' && translate('dailyGroupVoiceLimitReached')}
            {!reason && translate('unlockUnlimited')}
          </p>
        </div>

        <div style={{ backgroundColor: featureBg, borderRadius: '1rem', padding: '0.25rem 0', marginBottom: '1.5rem', border: `1px solid ${featureBorder}` }}>
          {[
            { icon: '✉️', label: translate('unlimitedMessages') },
            { icon: '🎤', label: translate('unlimitedVoiceNotes') },
            { icon: '👥', label: translate('unlimitedGroupMessages') },
            { icon: '🌍', label: translate('unlimitedCommunityMessages') },
          ].map((feature, i, arr) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: '0.75rem 1rem', color: featureText, fontSize: '0.875rem',
              borderBottom: i < arr.length - 1 ? `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` : 'none'
            }}>
              <span style={{ fontSize: '1.1rem' }}>{feature.icon}</span>
              {feature.label}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <button className="premium-plan-btn" onClick={() => handleUpgrade(MONTHLY_PLAN_CODE, 'monthly')} disabled={!paystackLoaded}
            style={{ padding: '1rem', background: 'linear-gradient(135deg, #008081, #005f5f)', color: '#FFFFFF', border: 'none', borderRadius: '0.875rem', fontWeight: '700', cursor: paystackLoaded ? 'pointer' : 'not-allowed', fontSize: '0.95rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 15px rgba(0,128,129,0.35)', opacity: paystackLoaded ? 1 : 0.7 }}>
            <span>{translate('monthly')}</span>
            <span style={{ fontSize: '1.1rem' }}>{translate('pricePerMonth', { price: 5 })}</span>
          </button>
          <button className="premium-plan-btn" onClick={() => handleUpgrade(SIX_MONTH_PLAN_CODE, 'sixMonth')} disabled={!paystackLoaded}
            style={{ padding: '1rem', background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)', color: '#FFFFFF', border: 'none', borderRadius: '0.875rem', fontWeight: '700', cursor: paystackLoaded ? 'pointer' : 'not-allowed', fontSize: '0.95rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 15px rgba(75,44,94,0.35)', opacity: paystackLoaded ? 1 : 0.7 }}>
            <span>{translate('sixMonths')} <span style={{ fontSize: '0.75rem', opacity: 0.75, marginLeft: '0.35rem' }}>{translate('savePercent', { percent: 33 })}</span></span>
            <span style={{ fontSize: '1.1rem' }}>{translate('priceTotal', { price: 20 })}</span>
          </button>
        </div>

        <button onClick={onClose} style={{ width: '100%', padding: '0.75rem', backgroundColor: 'transparent', border: `1px solid ${closeBtnBorder}`, borderRadius: '0.875rem', color: closeBtnText, cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>
          {translate('maybeLater')}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STRIKE / BAN WARNING MODAL
// Shown when a message is blocked by the server-side content filter.
// Theme-aware to match this component's light/dark support.
// ─────────────────────────────────────────────────────────────
function StrikeWarningModal({ strikes, banned, theme, onClose }) {
  const isDark = theme === 'dark';
  const textColor      = isDark ? '#ffffff' : '#1a1a2e';
  const textSecondary  = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
  const bgColor        = isDark ? 'linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))' : 'linear-gradient(145deg, #ffffff, #f0f0f0)';
  const trackBg        = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem',
      animation: 'fadeInUp 0.25s ease'
    }}>
      <div style={{
        background: bgColor,
        backdropFilter: 'blur(20px)',
        borderRadius: '1.5rem',
        padding: '2rem',
        maxWidth: '380px',
        width: '100%',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
        border: banned ? '1px solid rgba(224,92,92,0.3)' : '1px solid rgba(250,204,21,0.25)',
        textAlign: 'center'
      }}>
        <div style={{
          width: '64px', height: '64px', margin: '0 auto 1.25rem',
          background: banned ? 'rgba(224,92,92,0.15)' : 'rgba(250,204,21,0.12)',
          border: banned ? '1px solid rgba(224,92,92,0.3)' : '1px solid rgba(250,204,21,0.25)',
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.75rem'
        }}>
          {banned ? '🔒' : '💛'}
        </div>

        <h2 style={{ margin: 0, color: textColor, fontSize: '1.25rem', fontWeight: '800', letterSpacing: '-0.02em' }}>
          {banned ? 'Account disabled' : "Let's keep this space safe"}
        </h2>

        <p style={{ margin: '0.75rem 0 0', color: textSecondary, fontSize: '0.9rem', lineHeight: 1.7 }}>
          {banned
            ? "Your account has been permanently disabled after repeated messages that didn't meet Veyl's community guidelines."
            : "That message didn't go through — it didn't feel like it fit the space your group is building together. Take a breath, and feel free to try rephrasing."}
        </p>

        {!banned && (
          <div style={{
            marginTop: '1.25rem',
            display: 'flex', justifyContent: 'center', gap: '0.5rem'
          }}>
            {[1, 2, 3].map(n => (
              <div key={n} style={{
                width: '32px', height: '6px', borderRadius: '3px',
                backgroundColor: n <= strikes ? '#facc15' : trackBg
              }} />
            ))}
          </div>
        )}

        <button
          className="strike-modal-btn"
          onClick={onClose}
          style={{
            marginTop: '1.5rem', width: '100%', padding: '0.875rem',
            background: banned ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'linear-gradient(135deg, #008081, #005f5f)',
            color: banned ? textColor : '#ffffff',
            border: banned ? `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}` : 'none',
            borderRadius: '0.875rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem',
            boxShadow: banned ? 'none' : '0 4px 15px rgba(0,128,129,0.35)'
          }}
        >
          {banned ? 'Close' : 'Got it'}
        </button>
      </div>
    </div>
  );
}

export default function GroupView({ user, userData, setUserData, theme, t: themeTokens, translate: t }) {
  const { t: translateFunc } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVoiceNote, setShowVoiceNote] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [groupData, setGroupData] = useState(null);
  const [hoveredMsgId, setHoveredMsgId] = useState(null);
  const [deletingMsgId, setDeletingMsgId] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumReason, setPremiumReason] = useState(null);
  const [strikeWarning, setStrikeWarning] = useState(null); // { strikes, banned } | null
  const messagesEndRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  const isDark = theme === 'dark';

  const colors = {
    bg:               isDark ? 'rgba(255,255,255,0.02)' : '#ffffff',
    border:           isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
    headerBg:         isDark ? 'rgba(255,255,255,0.02)' : '#f8f9fa',
    text:             isDark ? '#ffffff' : '#1a1a2e',
    textSecondary:    isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
    textMuted:        isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
    msgAreaBg:        isDark ? 'rgba(0,0,0,0.15)' : '#f0f2f5',
    partnerMsgBg:     isDark ? 'rgba(255,255,255,0.07)' : '#e9ecef',
    partnerMsgBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    inputBg:          isDark ? 'rgba(255,255,255,0.05)' : '#ffffff',
    inputBorder:      isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)',
    inputColor:       isDark ? '#ffffff' : '#1a1a2e',
    btnBg:            isDark ? 'rgba(255,255,255,0.05)' : '#f1f3f4',
    btnBorder:        isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    btnText:          isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)',
    iconColor:        isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
  };

  const groupId      = userData?.groupId;
  const myAvatarId   = userData?.unveiled ? 'unveiled' : userData?.avatarId;
  const members      = groupData?.members || [];
  const isPremium    = userData?.isPremium;

  // Mirrors the backend's lazy-reset check (functions/index.js, isNewLocalDay)
  // so the counters shown here match what a real send would actually do,
  // instead of trusting Firestore fields that only update on send.
  const groupTimezone = userData?.timezone || 'UTC';
  const lastGroupReset = userData?.lastGroupReset;
  const groupCountersStale = isNewLocalDay(lastGroupReset, groupTimezone);

  const groupMsgsUsed  = groupCountersStale ? 0 : (userData?.groupMessagesUsedToday || 0);
  const groupVoiceUsed = groupCountersStale ? 0 : (userData?.groupVoiceNotesUsedToday || 0);
  const groupMsgsLeft  = isPremium ? 999 : (GROUP_MSG_LIMIT - groupMsgsUsed);
  const groupVoiceLeft = isPremium ? 999 : (GROUP_VOICE_LIMIT - groupVoiceUsed);

  const categories = [
    { key: 'fitnessHealth',    value: 'Fitness & Health' },
    { key: 'learningSkills',   value: 'Learning & Skills' },
    { key: 'businessCareer',   value: 'Business & Career' },
    { key: 'creativeProjects', value: 'Creative Projects' },
  ];

  const translateCategory = (categoryValue) => {
    const match = categories.find(c => c.value === categoryValue);
    return match ? t(match.key) : categoryValue;
  };

  useEffect(() => {
    if (!groupId) return;
    const unsubscribe = onSnapshot(doc(db, 'groups', groupId), (snap) => {
      if (snap.exists()) setGroupData({ id: snap.id, ...snap.data() });
    });
    return () => unsubscribe();
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return;
    const q = query(collection(db, 'groups', groupId, 'messages'), orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = [];
      snapshot.forEach(d => msgs.push({ id: d.id, ...d.data() }));
      const newCount = msgs.length;
      const oldCount = prevMessageCountRef.current;
      if (newCount > oldCount && oldCount > 0) {
        const latestMsg = msgs[msgs.length - 1];
        if (latestMsg.senderId !== user.uid) {
          // Only notify the current user, via Cloud Function
          const isCurrentUserAMember = members.some(m => m.uid === user.uid);
          if (isCurrentUserAMember) {
            const functions = getFunctions();
            const sendNotification = httpsCallable(functions, 'sendNotification');
            sendNotification({
              recipientId: user.uid,
              type: 'group_message',
              message: translateFunc('newGroupMessageNotification', {
                name: latestMsg.senderName || translateFunc('someone'),
              }),
            }).catch(err => console.error('Group notification error:', err));
          }
        }
      }
      prevMessageCountRef.current = newCount;
      setMessages(msgs);
    });
    return () => unsubscribe();
  }, [groupId, user.uid, members]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
  e.preventDefault();
  if (!newMessage.trim() || loading || !groupId) return;
  if (!isPremium && groupMsgsLeft <= 0) { setPremiumReason('groupMessages'); setShowPremiumModal(true); return; }
  setLoading(true);
  try {
    const functions = getFunctions();
    const sendGroupMessage = httpsCallable(functions, 'sendGroupMessage');
    await sendGroupMessage({ groupId, text: newMessage });
    setNewMessage('');
  } catch (err) {
    if (err.code === 'functions/resource-exhausted') {
      setPremiumReason('groupMessages');
      setShowPremiumModal(true);
    } else if (err.code === 'functions/failed-precondition' && err.details?.moderationBlocked) {
      setStrikeWarning({ strikes: err.details.strikes, banned: err.details.banned });
    } else {
      alert(t('error') + ': ' + err.message);
    }
  } finally {
    setLoading(false);
  }
};

  const deleteMessage = async (msgId) => {
    if (!groupId) return;
    setDeletingMsgId(msgId);
    try {
      await deleteDoc(doc(db, 'groups', groupId, 'messages', msgId));
    } catch (err) {
      alert(t('error') + ': ' + err.message);
    } finally {
      setDeletingMsgId(null);
    }
  };

  const handleLeave = async () => {
    setLeaving(true);
    try {
      await leaveGroup(user.uid, groupId);
      setUserData(prev => ({ ...prev, groupId: null }));
      setShowLeaveConfirm(false);
    } catch (err) {
      alert(t('error') + ': ' + err.message);
    } finally {
      setLeaving(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const shouldShowDateDivider = (messages, index) => {
    if (index === 0) return true;
    const prev = messages[index - 1].timestamp?.toDate?.();
    const curr = messages[index].timestamp?.toDate?.();
    if (!prev || !curr) return false;
    return prev.toDateString() !== curr.toDateString();
  };

  const wrapperStyle = {
    animation: 'fadeInUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
    color: colors.text,
    backgroundColor: colors.bg,
    borderRadius: '1.25rem',
    overflow: 'hidden',
    boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)',
  };

  if (!groupId) {
    return (
      <div style={wrapperStyle}>
        <style>{styleTag}</style>
        <GroupMatchingPage user={user} userData={userData} setUserData={setUserData} theme={theme} t={themeTokens} translate={t} />
      </div>
    );
  }

  if (showLeaveConfirm) {
    return (
      <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
        <style>{styleTag}</style>
        <div style={wrapperStyle}>
          <div style={{ padding: '2.5rem 2rem', textAlign: 'center' }}>
            <div style={{ width: '64px', height: '64px', margin: '0 auto 1.25rem', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.25)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem' }}>👋</div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '800', marginBottom: '0.75rem', color: colors.text, letterSpacing: '-0.02em' }}>{t('leaveGroupTitle')}</h2>
            <p style={{ color: colors.textMuted, fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '1.75rem' }}>{t('leaveGroupDesc')}</p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowLeaveConfirm(false)} style={{ flex: 1, padding: '0.875rem', border: `1px solid ${colors.btnBorder}`, backgroundColor: 'transparent', color: colors.btnText, borderRadius: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>{t('stay')}</button>
              <button onClick={handleLeave} disabled={leaving} style={{ flex: 1, padding: '0.875rem', backgroundColor: '#E05C5C', color: '#FFFFFF', border: 'none', borderRadius: '0.875rem', fontWeight: '700', cursor: leaving ? 'not-allowed' : 'pointer', opacity: leaving ? 0.6 : 1, boxShadow: '0 4px 15px rgba(224,92,92,0.3)' }}>
                {leaving ? t('leaving') : t('leaveGroup')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showMembers) {
    return (
      <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
        <style>{styleTag}</style>
        <div style={wrapperStyle}>
          <div style={{ padding: '1.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.75rem', fontWeight: '700', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t('groupMembers')}</h3>
              <button onClick={() => setShowMembers(false)} style={{ color: '#008081', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}>← {t('backToChat')}</button>
            </div>
            <p style={{ fontSize: '0.8rem', color: colors.textMuted, marginBottom: '1.5rem' }}>
              {members.length} / {groupData?.maxMembers || 5} · {translateCategory(groupData?.goalCategory)}
            </p>
            <div style={{ display: 'grid', gap: '0.6rem', marginBottom: '1.75rem' }}>
              {members.map((member, i) => (
                <div key={member.uid || i} className="group-member-card" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem', backgroundColor: member.uid === user.uid ? 'rgba(0,128,129,0.08)' : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'), border: member.uid === user.uid ? '1px solid rgba(0,128,129,0.25)' : `1px solid ${colors.border}`, borderRadius: '0.875rem' }}>
                  <Avatar username={member.name} avatarId={member.uid === user.uid ? myAvatarId : (member.avatarId || null)} size={40} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: colors.text, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      {member.name}
                      {member.uid === user.uid && <span style={{ fontSize: '0.65rem', color: '#008081', fontWeight: '700', backgroundColor: 'rgba(0,128,129,0.12)', padding: '0.1rem 0.4rem', borderRadius: '9999px' }}>{t('you')}</span>}
                      {member.uid === groupData?.createdBy && <span style={{ fontSize: '0.65rem', color: '#facc15', fontWeight: '700', backgroundColor: 'rgba(250,204,21,0.1)', padding: '0.1rem 0.4rem', borderRadius: '9999px' }}>{t('creator')}</span>}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: colors.textMuted, marginTop: '0.1rem' }}>
                      {t('joined')} {new Date(member.joinedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => { setShowMembers(false); setShowLeaveConfirm(true); }} style={{ width: '100%', padding: '0.875rem', backgroundColor: 'transparent', color: '#E05C5C', border: '1px solid rgba(224,92,92,0.35)', borderRadius: '0.875rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.875rem' }}>
              {t('leaveGroup')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{styleTag}</style>
      <div style={{ ...wrapperStyle, display: 'flex', flexDirection: 'column', height: '600px' }}>

        {/* Header */}
        <div style={{ padding: '1rem 1.25rem', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: colors.headerBg }}>
          <div>
            <div style={{ fontWeight: '700', color: colors.text, fontSize: '0.95rem' }}>{groupData?.name || t('group')}</div>
            <div style={{ fontSize: '0.78rem', color: colors.textSecondary, marginTop: '0.15rem', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem' }}>
              <span>{members.length} · {translateCategory(groupData?.goalCategory)}</span>
              {!isPremium && (
                <>
                  <span style={{ color: groupMsgsLeft <= 3 ? '#f87171' : colors.textMuted }}>· {groupMsgsLeft} {t('msgs')} · {groupVoiceLeft} 🎤</span>
                  <span onClick={() => { setPremiumReason(null); setShowPremiumModal(true); }} style={{ color: '#008081', cursor: 'pointer', fontWeight: '600' }}>· ✨ {t('upgrade')}</span>
                </>
              )}
              {isPremium && <span style={{ color: '#c084fc', fontWeight: '600' }}>· ✨ {t('premium')}</span>}
            </div>
          </div>
          <button className="group-header-btn" onClick={() => setShowMembers(true)} style={{ background: colors.btnBg, border: `1px solid ${colors.btnBorder}`, borderRadius: '0.5rem', padding: '0.4rem 0.75rem', cursor: 'pointer', color: colors.btnText, fontSize: '0.78rem', fontWeight: '500' }}>
            {t('membersBtn')}
          </button>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem', background: colors.msgAreaBg }}>
          {messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: colors.textMuted, paddingTop: '3rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>👋</div>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>{t('noGroupMessages')}</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const isMe = msg.senderId === user.uid;
              const isHovered = hoveredMsgId === msg.id;
              const isDeleting = deletingMsgId === msg.id;
              const msgAvatarId = isMe ? myAvatarId : (members.find(m => m.uid === msg.senderId)?.avatarId || null);
              return (
                <React.Fragment key={msg.id}>
                  {shouldShowDateDivider(messages, index) && (
                    <div style={{ textAlign: 'center', margin: '1rem 0' }}>
                      <span style={{ fontSize: '0.72rem', color: colors.textMuted, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', padding: '0.2rem 0.75rem', borderRadius: '9999px' }}>
                        {formatDate(msg.timestamp)}
                      </span>
                    </div>
                  )}
                  <div onMouseEnter={() => isMe && setHoveredMsgId(msg.id)} onMouseLeave={() => setHoveredMsgId(null)}
                    style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: '1rem', gap: '0.5rem', alignItems: 'flex-end', animation: 'messageIn 0.2s ease' }}>
                    {!isMe && <Avatar username={msg.senderName} avatarId={msgAvatarId} size={30} />}
                    <div style={{ maxWidth: '70%', display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                      {!isMe && <div style={{ fontSize: '0.68rem', color: colors.textMuted, marginBottom: '0.25rem', paddingLeft: '0.25rem' }}>{msg.senderName}</div>}
                      <div style={{ padding: '0.75rem 1rem', borderRadius: isMe ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem', background: isMe ? 'linear-gradient(135deg, #008081, #005f5f)' : colors.partnerMsgBg, color: isMe ? '#ffffff' : colors.text, border: isMe ? 'none' : `1px solid ${colors.partnerMsgBorder}`, opacity: isDeleting ? 0.4 : 1, transition: 'opacity 0.2s', boxShadow: isMe ? '0 4px 12px rgba(0,128,129,0.25)' : 'none' }}>
                        {(!msg.type || msg.type === 'text') && <span style={{ fontSize: '0.95rem', lineHeight: 1.5 }}>{msg.text}</span>}
                        {msg.type === 'voice' && <GroupVoiceNotePlayer audioUrl={msg.audioUrl} duration={msg.duration} modulation={msg.modulation} isDark={isDark} />}
                        <div style={{ fontSize: '0.68rem', opacity: 0.5, marginTop: '0.3rem', textAlign: isMe ? 'right' : 'left' }}>{formatTime(msg.timestamp)}</div>
                      </div>
                      {isMe && isHovered && !isDeleting && (
                        <button onClick={() => deleteMessage(msg.id)} style={{ marginTop: '0.25rem', padding: '0.15rem 0.5rem', backgroundColor: 'transparent', border: 'none', borderRadius: '999px', cursor: 'pointer', color: '#E05C5C', fontSize: '0.7rem', fontWeight: '500', opacity: 0.8 }}>{t('deleteMessage')}</button>
                      )}
                      {isDeleting && <span style={{ fontSize: '0.7rem', color: colors.textMuted, marginTop: '0.2rem' }}>{t('deleting')}</span>}
                    </div>
                    {isMe && <Avatar username={userData.name} avatarId={myAvatarId} size={30} />}
                  </div>
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Voice Note Panel */}
        {showVoiceNote && (
  <div style={{ padding: '0 1rem', background: colors.bg, borderTop: `1px solid ${colors.border}` }}>
    <VoiceNote user={user} userData={userData} chatId={groupId} groupMode={true} setUserData={setUserData} groupVoiceLeft={groupVoiceLeft}
      onSent={() => setShowVoiceNote(false)} />
  </div>
)}

        {/* Low message warning */}
        {!isPremium && groupMsgsLeft <= 3 && groupMsgsLeft > 0 && (
          <div style={{ padding: '0.5rem 1.25rem', background: 'rgba(250,204,21,0.06)', borderTop: '1px solid rgba(250,204,21,0.15)' }}>
            <p style={{ fontSize: '0.75rem', color: '#facc15', margin: 0, textAlign: 'center' }}>
              {translateFunc('groupMessagesLeftWarning', { count: groupMsgsLeft })}{' '}
              <span onClick={() => { setPremiumReason('groupMessages'); setShowPremiumModal(true); }} style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: '600' }}>{t('upgrade')}</span>{' '}
              {t('forUnlimited')}
            </p>
          </div>
        )}

        {/* Limit reached */}
        {!isPremium && groupMsgsLeft <= 0 && (
          <div style={{ padding: '0.75rem 1.25rem', borderTop: `1px solid ${colors.border}`, textAlign: 'center' }}>
            <button onClick={() => { setPremiumReason('groupMessages'); setShowPremiumModal(true); }} style={{ padding: '0.6rem 1.5rem', background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)', color: '#FFFFFF', border: 'none', borderRadius: '0.75rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem', boxShadow: '0 4px 15px rgba(75,44,94,0.35)' }}>
              ✨ {t('upgrade')}
            </button>
          </div>
        )}

        {/* Input */}
        <form onSubmit={sendMessage} style={{ padding: '1rem 1.25rem', borderTop: `1px solid ${colors.border}`, background: colors.bg, borderBottomLeftRadius: '1.25rem', borderBottomRightRadius: '1.25rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button type="button" className="group-icon-btn"
              onClick={() => {
                if (!isPremium && groupVoiceLeft <= 0) { setPremiumReason('groupVoice'); setShowPremiumModal(true); return; }
                setShowVoiceNote(!showVoiceNote);
              }}
              style={{ padding: '0.75rem', backgroundColor: showVoiceNote ? 'rgba(0,128,129,0.15)' : colors.btnBg, border: `1px solid ${showVoiceNote ? 'rgba(0,128,129,0.5)' : colors.btnBorder}`, borderRadius: '0.75rem', cursor: (!isPremium && groupVoiceLeft <= 0) ? 'not-allowed' : 'pointer', fontSize: '1.2rem', color: showVoiceNote ? '#008081' : colors.iconColor, flexShrink: 0, opacity: (!isPremium && groupVoiceLeft <= 0) ? 0.4 : 1 }}>
              🎤
            </button>
            <input type="text" className="group-input" value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
              placeholder={groupMsgsLeft <= 0 && !isPremium ? t('dailyLimitReached') : t('messageGroup')}
              disabled={!isPremium && groupMsgsLeft <= 0}
              style={{ flex: 1, padding: '0.75rem 1rem', border: `1px solid ${colors.inputBorder}`, borderRadius: '0.75rem', fontSize: '0.95rem', backgroundColor: colors.inputBg, color: colors.inputColor, opacity: (!isPremium && groupMsgsLeft <= 0) ? 0.4 : 1, boxSizing: 'border-box', transition: 'border-color 0.2s, box-shadow 0.2s' }} />
            <button type="submit" className="group-send-btn"
              disabled={loading || !newMessage.trim() || (!isPremium && groupMsgsLeft <= 0)}
              style={{ padding: '0.75rem 1.25rem', background: 'linear-gradient(135deg, #008081, #005f5f)', color: '#FFFFFF', borderRadius: '0.75rem', fontWeight: '700', border: 'none', cursor: (loading || !newMessage.trim() || (!isPremium && groupMsgsLeft <= 0)) ? 'not-allowed' : 'pointer', opacity: (loading || !newMessage.trim() || (!isPremium && groupMsgsLeft <= 0)) ? 0.4 : 1, flexShrink: 0, boxShadow: '0 4px 15px rgba(0,128,129,0.3)', fontSize: '0.9rem' }}>
              {t('send')}
            </button>
          </div>
        </form>
      </div>

      {showPremiumModal && (
        <PremiumModal
          onClose={() => setShowPremiumModal(false)}
          reason={premiumReason}
          theme={theme}
          userEmail={user.email}
          userId={user.uid}
        />
      )}

      {strikeWarning && (
        <StrikeWarningModal
          strikes={strikeWarning.strikes}
          banned={strikeWarning.banned}
          theme={theme}
          onClose={() => setStrikeWarning(null)}
        />
      )}
    </>
  );
}

function GroupVoiceNotePlayer({ audioUrl, duration, modulation, isDark }) {
  const { t: translate } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current || !audioUrl) return;
    if (isPlaying) { audioRef.current.pause(); setIsPlaying(false); }
    else { audioRef.current.play(); setIsPlaying(true); }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!audioUrl) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span>❌</span>
      <span style={{ fontSize: '0.875rem', color: isDark ? '#ffffff' : '#1a1a2e' }}>{translate('failedToLoad')}</span>
    </div>
  );

  const progress        = duration ? (currentTime / duration) * 100 : 0;
  const playerTextColor = isDark ? '#ffffff' : '#1a1a2e';
  const playerBtnBg     = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
  const progressBg      = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)';
  const progressFill    = isDark ? '#ffffff' : '#008081';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: '180px', padding: '0.25rem' }}>
      <button onClick={togglePlay} style={{ width: '34px', height: '34px', borderRadius: '50%', border: 'none', backgroundColor: playerBtnBg, color: playerTextColor, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', flexShrink: 0 }}>
        {isPlaying ? '⏸' : '▶'}
      </button>
      <div style={{ flex: 1 }}>
        <div style={{ height: '3px', backgroundColor: progressBg, borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, backgroundColor: progressFill, transition: 'width 0.1s linear' }} />
        </div>
        <div style={{ fontSize: '0.68rem', marginTop: '0.25rem', display: 'flex', justifyContent: 'space-between', opacity: 0.6, color: playerTextColor }}>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration || 0)}</span>
        </div>
      </div>
      <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>
        {modulation === 'deeper' && '🔉'}
        {modulation === 'neutral' && '🎭'}
        {modulation === 'higher' && '🔊'}
      </span>
      <audio ref={audioRef} src={audioUrl}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onEnded={() => { setIsPlaying(false); setCurrentTime(0); }}
        style={{ display: 'none' }} />
    </div>
  );
}